// Login.js
import React, { useContext, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { AuthContext } from './AuthContext'
import './rainbow.css'
import {
  Flex,
  VStack,
  Box,
  HStack,
  Heading,
  Text,
  Button,
  Link,
  Image,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  useColorModeValue,
  useToast,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react'
import { FaGoogle, FaEye, FaEyeSlash } from 'react-icons/fa'

import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth'
import { firebase } from './firebaseConfig'
import { useNavigate, useLocation } from 'react-router-dom'

import { fetchWithoutAuth, fetchWithAuth } from './apiClient'
import { Capacitor } from '@capacitor/core';
import { initNativePush } from './push-native.js';
import { trackEvent } from './analytics';

import designImage from './img/design-1-alpha.png'

// MUI imports & cache
import { CacheProvider } from '@emotion/react'
import createCache from '@emotion/cache'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import Timeline from '@mui/lab/Timeline'
import TimelineItem from '@mui/lab/TimelineItem'
import TimelineOppositeContent, {
  timelineOppositeContentClasses,
} from '@mui/lab/TimelineOppositeContent'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineContent from '@mui/lab/TimelineContent'
import Typography from '@mui/material/Typography'
import { LuTriangleAlert, LuCircleCheckBig } from 'react-icons/lu'

import { v4 as uuidv4 } from 'uuid'
import ResetPasswordModal from './components/ResetPasswordModal'
import AuthPageLayout from './components/AuthPageLayout'
import ExistingUserCard from './components/ExistingUserCard'
import BetaBanner from './components/BetaBanner'
import WaitlistForm from './components/WaitlistForm'
import AuthCard from './components/AuthCard'

const muiCache = createCache({ key: 'mui', prepend: true })
const muiTheme = createTheme()

const sampleActivities = [
  { firedTime: '4/20 6:51 AM', details: 'Completed!', retryCount: '' },
  { firedTime: '4/20 6:50 AM', details: 'Have you taken that med yet?', retryCount: 2 },
]
const iconStyle = { fontSize: 24, verticalAlign: 'middle' }
const MotionFlex = motion(Flex)

function generateRandomUsername() {
  return 'user_' + uuidv4().slice(0, 8)
}

const isBackendOfflineError = err => {
  if (!err || err.status) return false
  const message = typeof err.message === 'string' ? err.message.toLowerCase() : ''
  const code = typeof err.code === 'string' ? err.code.toLowerCase() : ''
  const offlineSnippets = ['failed to fetch', 'network request failed', 'network error', 'load failed']
  const messageIndicatesOffline = offlineSnippets.some(snippet => message.includes(snippet))
  return (
    code === 'err_network' ||
    (err.name === 'TypeError' && messageIndicatesOffline) ||
    messageIndicatesOffline
  )
}

export default function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  // Always default to /dashboard, never /
  const from = location.state?.from?.pathname || '/dashboard'

  const { user, loading: authLoading } = useContext(AuthContext)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showWaitlist, setShowWaitlist] = useState(false)

  const { isOpen, onOpen, onClose } = useDisclosure()

  const auth = getAuth(firebase)
  const toast = useToast()

  const bgOuter = 'bg.canvas'
  const textSecondary = 'text.muted'
  const accentBlue = useColorModeValue('brand.600', 'brand.200')
  const googleButtonBg = useColorModeValue('bg.subtle', 'bg.elevated')
  const googleHoverBg = useColorModeValue('gray.200', 'gray.600')

  useEffect(() => {
    if (!authLoading && user && !isLoading) {
      navigate(from, { replace: true })
    }
  }, [authLoading, user, isLoading, navigate, from])

  const handleServerGateFailure = err => {
    if (!err) return false

    if (isBackendOfflineError(err)) {
      setShowWaitlist(false)
      toast({
        title: 'Service unavailable',
        description: 'Our servers are unreachable right now. Please check back in a few minutes.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      })
      return true
    }

    const code = typeof err.code === 'string' ? err.code : ''
    const rawMessage = typeof err.message === 'string' ? err.message : ''
    const messageLower = rawMessage.toLowerCase()
    if (code.startsWith('auth/')) {
      setShowWaitlist(false)
      return false
    }

    const isCapacityError =
      code === 'USER_LIMIT_EXCEEDED' ||
      messageLower.includes('beta is currently full') ||
      messageLower.includes('at capacity')

    setShowWaitlist(isCapacityError)

    const message = isCapacityError
      ? 'We are at capacity right now. Please try again later.'
      : rawMessage || 'Unable to finish signing you in. Please try again.'

    toast({
      title: isCapacityError ? 'Beta is currently full' : 'Sign-in failed',
      description: message,
      status: 'error',
      duration: 5000,
      isClosable: true,
    })
    return true
  }

// shared token‐exchange + redirect
  async function postSignIn(firebaseUser, method) {
    try {
      const token = await firebaseUser.getIdToken()
      await fetchWithoutAuth('/firebase/auth', {
        method: 'POST',
        body: JSON.stringify({ token }),
      // credentials removed; header-only auth
      })
      await fetchWithAuth('/user-settings', { method: 'GET' })

      // Fire-and-forget native push registration on Android/iOS apps
      const apiBase = process.env.REACT_APP_BACKEND_URL // absolute URL
      if (Capacitor.isNativePlatform() && apiBase) {
        // We'll switch initNativePush to accept a getter; no storage.
        initNativePush(apiBase, async () => await firebaseUser.getIdToken())
      }
      trackEvent('auth_login', { method: method || firebaseUser?.providerData?.[0]?.providerId || 'unknown' });
      navigate(from, { replace: true })
    } catch (err) {
      await signOut(auth)
      throw err
    }
  }

  // Google
  async function handleGoogleLogin() {
    setIsLoading(true)
    try {
      const { user, providerData } = await signInWithPopup(auth, new GoogleAuthProvider())
      await postSignIn(user, 'google')
    } catch (err) {
      if (handleServerGateFailure(err)) return
      console.error('Google sign-in failed', err)
      trackEvent('auth_login_error', { method: 'google', code: err?.code })
      toast({
        title: 'Google sign-in failed.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Email/Password Sign-Up
  async function handleEmailSignUp() {
    setIsLoading(true)
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password)
      await postSignIn(user, 'email_signup')
    } catch (err) {
      if (handleServerGateFailure(err)) return
      console.error('Email sign-up failed', err)
      trackEvent('auth_signup_error', { method: 'email_password', code: err?.code })
      const msg =
        err.code === 'auth/email-already-in-use'
          ? 'That email is already in use.'
          : 'Sign-up error. Please try again.'
      toast({ title: msg, status: 'error', duration: 3000, isClosable: true })
    } finally {
      setIsLoading(false)
    }
  }

  // Email/Password Sign-In
  async function handleEmailSignIn() {
    setIsLoading(true)
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password)
      // For sign-in, we do not update displayName here
      await postSignIn(user, 'email_password')
    } catch (err) {
      if (handleServerGateFailure(err)) return
      console.error('Email sign-in failed', err)
      trackEvent('auth_login_error', { method: 'email_password', code: err?.code })
      let msg = 'Sign-in error. Please try again.'
      if (err.code === 'auth/user-not-found') {
        msg = 'No account found for that email.'
      } else if (err.code === 'auth/wrong-password') {
        msg = 'Incorrect password.'
      }
      toast({ title: msg, status: 'error', duration: 3000, isClosable: true })
    } finally {
      setIsLoading(false)
    }
  }

  // Anonymous
  // Removed anonymous sign-in function as guest login is disabled

  const existingSection = (
    <ExistingUserCard>
      <Box>
        <Box mb={4}>
          <Heading size="lg" mb={1}>
            Sign in or create an account
          </Heading>
        </Box>
        {/* Auth Form */}
        <VStack spacing={3} w="100%">
          <Input
            placeholder="you@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />

          <InputGroup>
            <Input
              placeholder="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
            <InputRightElement>
              <IconButton
                size="sm"
                variant="ghost"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                icon={<Icon as={showPassword ? FaEyeSlash : FaEye} />}
                onClick={() => setShowPassword(v => !v)}
              />
            </InputRightElement>
          </InputGroup>

          <Text
            as="button"
            color={accentBlue}
            fontSize="sm"
            alignSelf="flex-start"
            mb={2}
            onClick={onOpen}
            _hover={{ textDecoration: 'underline' }}
          >
            Forgot password?
          </Text>

          <HStack spacing={2} w="100%">
            <Button
              flex={1}
              colorScheme="brand"
              onClick={handleEmailSignUp}
              isLoading={isLoading}
              loadingText="Signing up"
            >
              Sign up with Email
            </Button>
            <Button
              flex={1}
              variant="outline"
              colorScheme="gray"
              onClick={handleEmailSignIn}
              isLoading={isLoading}
              loadingText="Signing in"
            >
              Sign in with Email
            </Button>
          </HStack>


          <Button
            onClick={handleGoogleLogin}
            leftIcon={<Icon as={FaGoogle} boxSize={5} />}
            bg={googleButtonBg}
            color="text.primary"
            _hover={{ bg: googleHoverBg }}
            w="100%"
            isLoading={isLoading}
            loadingText="Signing in"
          >
            Sign in with Google
          </Button>
        </VStack>
      </Box>
    </ExistingUserCard>
  )

  return (
    <MotionFlex
      position="relative"
      direction="column"
      align="center"
      justify="center"
      bg={bgOuter}
      color="text.primary"
      px={4}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      {isLoading && (
        <Box position="absolute" top="0" left="0" width="100%" height="100%" bg="blackAlpha.500" zIndex={10} display="flex" alignItems="center" justifyContent="center">
          <Spinner size="xl" color="white" />
        </Box>
      )}

      <Box w="100%" maxW="640px" px={4} mt={{ base: 6, md: 10 }} mb={6}>
        <BetaBanner />
      </Box>

      <AuthPageLayout existing={existingSection} />

      {showWaitlist && (
        <Box w="100%" maxW="640px" px={4} mt={4}>
          <AuthCard>
            <Heading size="md" mb={1}>Beta is currently full</Heading>
            <Text color={textSecondary} mb={4}>
              Join the waitlist and we&apos;ll notify you as soon as we open more spots.
            </Text>
            <WaitlistForm />
          </AuthCard>
        </Box>
      )}

      <Box mt={4} width="100%">
        <Text fontSize="sm" color={textSecondary} textAlign="center" mx="auto" maxW="720px">
          By interacting with this page or submitting information you agree to our{' '}
          <Link href="https://ekkos.io/terms.html" color={accentBlue} isExternal>Terms of Service</Link>,{' '}
          <Link href="https://ekkos.io/privacy.html" color={accentBlue} isExternal>Privacy Policy</Link>, and{' '}
          <Link href="https://ekkos.io/cookies.html" color={accentBlue} isExternal>Cookie Policy</Link>.
        </Text>
      </Box>

      <ResetPasswordModal isOpen={isOpen} onClose={onClose} />
    </MotionFlex>
  )
}
